import { motion } from "framer-motion";
import { Phone, Mail, MessageCircle, Instagram, Facebook, MapPin } from "lucide-react";
import { BRAND, waLink, mailtoLink, telLink } from "@/data/site";
import { CONTACT } from "@/constants/testIds";
import ServiceArea from "@/components/ServiceArea";

const Contact = () => {
  return (
    <>
    <section className="pt-40 md:pt-48 pb-24 md:pb-32 bg-white">
      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9 }}
          className="lg:col-span-7"
        >
          <p className="eyebrow mb-4">Contact</p>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.05]">
            Laten we <span className="italic text-gold">samen</span> iets moois maken.
          </h1>
          <p className="mt-6 text-neutral-600 max-w-lg leading-relaxed">
            Persoonlijk contact werkt het snelst. Kies wat het beste past — WhatsApp voor snelle
            antwoorden, telefoon voor een echt gesprek, e-mail voor uitgebreide info.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-12 max-w-2xl">
            <a
              data-testid={CONTACT.whatsappBtn}
              href={waLink()}
              target="_blank"
              rel="noreferrer"
              className="group flex flex-col items-center text-center border border-neutral-200 p-6 hover:border-gold hover:bg-beige transition-all"
            >
              <MessageCircle size={26} className="text-gold" strokeWidth={1.4} />
              <p className="mt-4 font-serif text-lg">WhatsApp</p>
              <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mt-1">
                Direct antwoord
              </p>
            </a>
            <a
              data-testid={CONTACT.phoneBtn}
              href={telLink}
              className="group flex flex-col items-center text-center border border-neutral-200 p-6 hover:border-gold hover:bg-beige transition-all"
            >
              <Phone size={26} className="text-gold" strokeWidth={1.4} />
              <p className="mt-4 font-serif text-lg">Bel</p>
              <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mt-1">
                {BRAND.phoneDisplay}
              </p>
            </a>
            <a
              data-testid={CONTACT.emailBtn}
              href={mailtoLink()}
              className="group flex flex-col items-center text-center border border-neutral-200 p-6 hover:border-gold hover:bg-beige transition-all"
            >
              <Mail size={26} className="text-gold" strokeWidth={1.4} />
              <p className="mt-4 font-serif text-lg">E-mail</p>
              <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mt-1">
                Uitgebreid
              </p>
            </a>
          </div>

          <div className="mt-14 flex items-center gap-5">
            <span className="text-xs uppercase tracking-[0.3em] text-neutral-500">Volg</span>
            <a href={BRAND.social.instagram} target="_blank" rel="noreferrer" className="text-ink hover:text-gold transition-colors">
              <Instagram size={20} />
            </a>
            <a href={BRAND.social.facebook} target="_blank" rel="noreferrer" className="text-ink hover:text-gold transition-colors">
              <Facebook size={20} />
            </a>
          </div>
        </motion.div>

        <motion.aside
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.15 }}
          className="lg:col-span-5 bg-beige p-10"
        >
          <p className="eyebrow mb-4">Contact</p>
          <p className="font-serif text-2xl leading-snug">{BRAND.fullName}</p>
          <div className="mt-6 space-y-3 text-sm text-neutral-700">
            <p className="flex items-start gap-3">
              <MapPin size={14} className="text-gold mt-1" />
              <span>{BRAND.address}</span>
            </p>
            <p className="flex items-start gap-3">
              <Phone size={14} className="text-gold mt-1" />
              <span>{BRAND.phoneDisplay}</span>
            </p>
            <p className="flex items-start gap-3">
              <Mail size={14} className="text-gold mt-1" />
              <span>{BRAND.email}</span>
            </p>
          </div>

          <div className="mt-8 pt-8 border-t border-neutral-200">
            <p className="eyebrow mb-4">Openingsuren</p>
            <ul className="text-sm text-neutral-700 space-y-1.5">
              {BRAND.openingHours.map((h) => (
                <li key={h.day} className="flex justify-between">
                  <span className="text-neutral-500">{h.day}</span>
                  <span>{h.hours}</span>
                </li>
              ))}
            </ul>
          </div>

          <p className="font-script text-gold text-4xl mt-10 leading-none">Tot binnenkort.</p>
        </motion.aside>
      </div>
    </section>
    <ServiceArea />
    </>
  );
};

export default Contact;
